import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Stack, Chip, Skeleton, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { tableFromIPC } from "apache-arrow";
import { geoPath, geoMercator } from "d3-geo";
import {
  BarCompareConstNat,
  BestPMGrid,
  TopThreePrioritiesMini,
  getTopVI,
  loadGroupData,
} from "./utils/metricsClient";
import { getPolConfig } from "./utils/polAggregators";
import ElderlyIcon from "@mui/icons-material/Elderly";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SchoolIcon from "@mui/icons-material/School";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HomeIcon from "@mui/icons-material/Home";

/* -------------------------------------------------------------------------- */
/* CONSTANTS                                                                   */
/* -------------------------------------------------------------------------- */

const US_PARTY_COLOURS = {
  Republican: "#CC2936",
  Democrat: "#0057A8",
  Independent: "#6B7280",
  Other: "#6B7280",
};

const normalizeUSParty = (raw = "") => {
  const s = String(raw).trim().toLowerCase();
  if (s.includes("republican") || s === "r" || s === "rep") return "Republican";
  if (s.includes("democrat") || s === "d" || s === "dem") return "Democrat";
  if (s.includes("independent")) return "Independent";
  return "Other";
};

const usPartyColor = (name) =>
  US_PARTY_COLOURS[normalizeUSParty(name)] || US_PARTY_COLOURS.Other;

const STATE_ABBR_TO_FIPS = {
  AL: "01", AK: "02", AZ: "04", AR: "05", CA: "06",
  CO: "08", CT: "09", DE: "10", DC: "11", FL: "12",
  GA: "13", HI: "15", ID: "16", IL: "17", IN: "18",
  IA: "19", KS: "20", KY: "21", LA: "22", ME: "23",
  MD: "24", MA: "25", MI: "26", MN: "27", MS: "28",
  MO: "29", MT: "30", NE: "31", NV: "32", NH: "33",
  NJ: "34", NM: "35", NY: "36", NC: "37", ND: "38",
  OH: "39", OK: "40", OR: "41", PA: "42", RI: "44",
  SC: "45", SD: "46", TN: "47", TX: "48", UT: "49",
  VT: "50", VA: "51", WA: "53", WV: "54", WI: "55",
  WY: "56",
};

const rowsFromTable = (table) => table.toArray().map((row) => row.toJSON());

const HOUSE_PARTY_COLORS = {
  Republican: "#CC2936",
  Democrat: "#0057A8",
  Independent: "#6B7280",
  Other: "#6B7280",
  R: "#CC2936",
  D: "#0057A8",
  I: "#6B7280",
};

const normalizeHouseParty = (raw = "") => {
  const s = String(raw).trim().toLowerCase();
  if (s.startsWith("r") || s.includes("republican")) return "Republican";
  if (s.startsWith("d") || s.includes("democrat")) return "Democrat";
  if (s.includes("independent")) return "Independent";
  return "Other";
};

const findSelectedCdFeature = (features, selectedId, district) => {
  const dist = String(district ?? "").padStart(2, "0");
  return features.find((f) => {
    const gid = String(f?.properties?.GEOID || f?.properties?.geoid || "");
    return gid === selectedId
      || gid === dist
      || gid === dist.padStart(3, "0")
      || gid === dist.padStart(5, "0")
      || gid.endsWith(dist.padStart(3, "0"))
      || gid.endsWith(dist.padStart(2, "0"));
  }) || (dist === "00" ? features[0] : null);
};

const buildDistrictMapProjection = ({ features, selectedFeature, width, height }) => {
  const projection = geoMercator();
  const path = geoPath(projection);
  try {
    projection.fitExtent(
      [[width * 0.03, height * 0.03], [width * 0.97, height * 0.97]],
            { type: "FeatureCollection", features }
    );
    if (selectedFeature) {
      const bounds = path.bounds(selectedFeature);
      const currentPx = Math.max(bounds[1][0] - bounds[0][0], bounds[1][1] - bounds[0][1]);
      const zoom = currentPx > 0
        ? Math.min(3.5, Math.max(1, (Math.min(width, height) * 0.54) / currentPx))
        : 1;
      projection.scale(projection.scale() * zoom);
      projection.translate([width / 1.6, height / 2]);
      const centroid = path.centroid(selectedFeature);
      projection.translate([width - centroid[0], height - centroid[1]]);
    }
  } catch {}
  return { projection, path: geoPath(projection) };
};

function PastVoteCDMap({ stateFips, district, houseParty, width = 320, height = 220 }) {
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        const url = process.env.PUBLIC_URL + "/usmaps/us_cds_2024_simplified.json";
        const res = await fetch(url);
        if (!res.ok) throw new Error("geo fetch failed");
        const json = await res.json();
        if (isActive) setGeo(json);
      } catch {
        if (isActive) setGeo(null);
      }
    };
    load();
    return () => { isActive = false; };
  }, []);

  if (!geo || !stateFips || district == null) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          border: "1px dashed #ddd",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafa",
        }}
      >
        <Typography variant="caption" color="text.disabled">
          Past vote map — loading
        </Typography>
      </Box>
    );
  }

  const fips = String(stateFips).padStart(2, "0");
  const dist = String(district).padStart(2, "0");
  const features = (geo.features || []).filter((f) => f?.properties?.STATEFP === fips);
  const selectedId = `${fips}${dist}`;
  const selectedFeature = findSelectedCdFeature(features, selectedId, district);
  const { path } = buildDistrictMapProjection({ features, selectedFeature, width, height });

  const party = normalizeHouseParty(houseParty);
  const highlight = HOUSE_PARTY_COLORS[party] || "#111827";
  const baseFill = alpha(highlight, 0.12);
  const baseStroke = alpha(highlight, 0.2);

  return (
    <svg width={width} height={height} style={{ display: "block", width: "100%", height: "100%" }}>
      {features.map((f, i) => {
        const isSelected = f?.properties?.GEOID === selectedId;
        return (
          <path
            key={i}
            d={path(f) || ""}
            fill={isSelected ? alpha(highlight, 0.35) : baseFill}
            stroke={isSelected ? highlight : baseStroke}
            strokeWidth={isSelected ? 2.2 : 0.8}
          />
        );
      })}
    </svg>
  );
}

function VIStateMap({ stateFips, district, viRows, width = 320, height = 220 }) {
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        const url = process.env.PUBLIC_URL + "/usmaps/us_cds_2024_simplified.json";
        const res = await fetch(url);
        if (!res.ok) throw new Error("geo fetch failed");
        const json = await res.json();
        if (isActive) setGeo(json);
      } catch {
        if (isActive) setGeo(null);
      }
    };
    load();
    return () => { isActive = false; };
  }, []);

  // Not ready yet — same placeholder style as PastVoteCDMap
  if (!geo || !stateFips || !viRows?.length) {
    return (
      <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" color="text.disabled">Loading…</Typography>
      </Box>
    );
  }

  const fips = String(stateFips).padStart(2, "0");
  const abbr = Object.keys(STATE_ABBR_TO_FIPS).find((k) => STATE_ABBR_TO_FIPS[k] === fips) || "";
  const features = (geo.features || []).filter((f) => f?.properties?.STATEFP === fips);

  const dist = String(district ?? "").padStart(2, "0");
  const selectedId = `${fips}${dist}`;
  const selectedFeature = findSelectedCdFeature(features, selectedId, district);
  const { path: pathGen } = buildDistrictMapProjection({ features, selectedFeature, width, height });

  const viByCd = new Map();
  viRows.forEach((r) => {
    viByCd.set(String(r?.state_CD119 || "").toUpperCase(), r);
  });

  const lerp = (a, b, t) => a + (b - a) * t;
  const toColor = (v) => {
    const t = Math.max(-1, Math.min(1, v * 2.5));
    if (t >= 0) {
      return `rgb(${Math.round(lerp(229,0,t))},${Math.round(lerp(231,87,t))},${Math.round(lerp(235,168,t))})`;
    }
    const tt = Math.abs(t);
    return `rgb(${Math.round(lerp(248,204,tt))},${Math.round(lerp(215,41,tt))},${Math.round(lerp(218,54,tt))})`;
  };

  const getMargin = (dist) => {
    const padded = String(dist).padStart(2, "0");
    const keys = [`${abbr}-${padded}`, `${abbr}-${Number(dist)}`, padded === "00" ? `${abbr}-AL` : null]
      .filter(Boolean).map((k) => k.toUpperCase());
    const row = keys.map((k) => viByCd.get(k)).find(Boolean);
    if (!row) return null;
    return Number(row.dem_pct ?? 0) - Number(row.rep_pct ?? 0);
  };

  return (
    <svg width={width} height={height} style={{ display: "block", width: "100%", height: "100%" }}>
      {features.map((f, i) => {
        const geoid = String(f?.properties?.GEOID || "");
        const dist = geoid.slice(2, 4);
        const isSelected = f === selectedFeature;
        const margin = getMargin(dist);
        const fill = margin == null ? "#f3f4f6" : toColor(margin);
        return (
          <path
            key={i}
            d={pathGen(f) || ""}
            fill={isSelected ? fill : "#f1f5f9"}
            fillOpacity={isSelected ? 1 : 0.5}
            stroke={isSelected ? "#475569" : "#cbd5e1"}
            strokeWidth={isSelected ? 1.5 : 0.4}
          />
        );
      })}
    </svg>
  );
}

function PastVsViBar({ pastDem, pastRep, viDem, viRep }) {
  const hasPast = pastDem != null && pastRep != null;
  const hasVI   = viDem   != null && viRep   != null;

  if (!hasPast && !hasVI) return null;

  const pct = (v) => v != null ? Number(v) * 100 : null;
  const pd = pct(pastDem), pr = pct(pastRep);

  const rawVd = pct(viDem), rawVr = pct(viRep);
  const viTotal = (rawVd ?? 0) + (rawVr ?? 0);
  const vd = viTotal > 0 && rawVd != null ? (rawVd / viTotal) * 100 : rawVd;
  const vr = viTotal > 0 && rawVr != null ? (rawVr / viTotal) * 100 : rawVr;

  const dDelta = hasVI && hasPast ? vd - pd : null;
  const rDelta = hasVI && hasPast ? vr - pr : null;

  const Row = ({ label, dem, rep, isVI }) => {
    if (dem == null || rep == null) return null;
    const demW = `${dem.toFixed(0)}%`;
    const repW = `${rep.toFixed(0)}%`;
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#1d4ed8" }}>D {demW}</Typography>
          <Typography sx={{
            fontSize: "0.58rem", fontWeight: 600, color: "#64748b",
            px: "6px", py: "1px", borderRadius: "4px",
            bgcolor: isVI ? "#f0f9ff" : "#f8fafc",
            border: isVI ? "1px solid #bae6fd" : "1px solid #e8edf4",
          }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#b91c1c" }}>R {repW}</Typography>
        </Box>
        <Box sx={{ height: 14, borderRadius: "3px", overflow: "hidden", display: "flex", border: "1px solid #e8edf4" }}>
          <Box sx={{ width: `${dem}%`, bgcolor: "#2563eb" }} />
          <Box sx={{ flex: 1, bgcolor: "#f1f5f9" }} />
          <Box sx={{ width: `${rep}%`, bgcolor: "#dc2626" }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
      {hasPast && <Row label="2024 Result" dem={pd} rep={pr} isVI={false} />}
      {hasVI   && <Row label="Vote Intention" dem={vd} rep={vr} isVI={true} />}

      {dDelta != null && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "2px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <Typography sx={{ fontSize: "0.58rem", color: dDelta > 0 ? "#1d4ed8" : "#6b7280", fontWeight: 700 }}>
              D {dDelta > 0 ? "▲" : "▼"} {Math.abs(dDelta).toFixed(1)}pp
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.56rem", color: "#94a3b8" }}>shift vs 2024</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <Typography sx={{ fontSize: "0.58rem", color: rDelta > 0 ? "#b91c1c" : "#6b7280", fontWeight: 700 }}>
              R {rDelta > 0 ? "▲" : "▼"} {Math.abs(rDelta).toFixed(1)}pp
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function MovementCard({ title, cdVal, stVal, cdDelta, stDelta, diffDelta, loading, showDeltaAsPrimary = false }) {
  if (loading) return (
    <Box sx={{
      border: "1px solid #e8edf4", borderRadius: "8px", p: "12px", bgcolor: "#fff", minHeight: 80,
      display: "flex", flexDirection: "column", gap: "8px",
    }}>
      <Skeleton variant="text" width="50%" height={11} />
      <Skeleton variant="text" width="70%" height={28} />
      <Skeleton variant="text" width="90%" height={10} />
    </Box>
  );

  const hasCurrent = cdVal != null;
  // Only show wave delta badges when there are genuinely multiple waves (delta is non-null)
  // Also guard against implausibly large deltas (>20pp) which indicate the current value
  // has been passed in the delta slot — treat those as single-wave
  const hasWaveDelta = cdDelta != null && Math.abs(cdDelta) <= 20;
  const curGap = hasCurrent && stVal != null ? cdVal - stVal : null;

  // Accent: direction of travel if tracked, else CD vs state gap
  const accentColor = hasWaveDelta
    ? (cdDelta > 0 ? "#059669" : "#dc2626")
    : curGap != null
    ? (curGap > 0 ? "#059669" : curGap < 0 ? "#dc2626" : "#64748b")
    : "#64748b";

  const fmt = (v) => v != null && Number.isFinite(v) ? `${v.toFixed(1)}%` : "—";
  const fmtPP = (v) => v != null && Number.isFinite(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}pp` : null;
  const primaryCd = showDeltaAsPrimary && hasWaveDelta ? cdDelta : cdVal;
  const primarySt = showDeltaAsPrimary && stDelta != null ? stDelta : stVal;
  const primaryFmt = showDeltaAsPrimary ? fmtPP : fmt;
  const primaryLeftLabel = showDeltaAsPrimary ? "District change" : "District";
  const primaryRightLabel = showDeltaAsPrimary ? "State change" : "State";

  // Plain-English narrative sentence — the key comms-facing content
  const narrative = (() => {
    if (showDeltaAsPrimary && hasWaveDelta) {
      if (stDelta != null) {
        const gap = cdDelta - stDelta;
        const relation = Math.abs(gap) < 0.5
          ? "in line with the state trend"
          : `${Math.abs(gap).toFixed(1)}pp ${gap > 0 ? "more" : "less"} than the state trend`;
        return `District change is ${fmtPP(cdDelta)} versus ${fmtPP(stDelta)} statewide, ${relation}.`;
      }
      return `District change is ${fmtPP(cdDelta)}.`;
    }
    if (!hasCurrent) return null;

    // Single-wave: no direction of travel — just show current position vs state
    if (!hasWaveDelta) {
      if (curGap == null) return `Currently at ${fmt(cdVal)}.`;
      const posText = Math.abs(curGap) < 1
        ? "in line with the state"
        : `${Math.abs(curGap).toFixed(1)}pp ${curGap > 0 ? "above" : "below"} the state`;
      return `Currently at ${fmt(cdVal)}, ${posText} (${fmt(stVal)}).`;
    }

    // Multi-wave: show direction of travel + current position
    const dirCD = cdDelta > 0 ? "rising" : "falling";
    const dirSt = stDelta != null ? (stDelta > 0 ? "rising" : "falling") : null;
    let story = "";
    if (dirSt && dirCD === dirSt) {
      story = `District is ${dirCD} (${cdDelta > 0 ? "+" : ""}${cdDelta.toFixed(1)}pp), mirroring the state (${stDelta > 0 ? "+" : ""}${stDelta.toFixed(1)}pp).`;
    } else if (dirSt) {
      story = `District is ${dirCD} (${cdDelta > 0 ? "+" : ""}${cdDelta.toFixed(1)}pp) while the state is ${dirSt} (${stDelta > 0 ? "+" : ""}${stDelta.toFixed(1)}pp) — diverging.`;
    } else {
      story = `District is ${dirCD} (${cdDelta > 0 ? "+" : ""}${cdDelta.toFixed(1)}pp).`;
    }
    if (curGap != null) {
      const posText = Math.abs(curGap) < 1
        ? "in line with the state"
        : `${Math.abs(curGap).toFixed(1)}pp ${curGap > 0 ? "above" : "below"} the state`;
      story += ` Currently at ${fmt(cdVal)}, ${posText} (${fmt(stVal)}).`;
    }
    return story;
  })();

  return (
    <Box sx={{
      border: "1px solid #e8edf4",
      borderRadius: "8px",
      p: "11px 13px",
      bgcolor: "#fff",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
    }}>
      {/* Title */}
      <Typography sx={{
        fontSize: "0.60rem", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.09em", color: "#94a3b8",
      }}>
        {title}
      </Typography>

      {(showDeltaAsPrimary ? hasWaveDelta : hasCurrent) ? (
        <>
          {/* Main value row: big district figure + state comparison */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: accentColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {primaryFmt(primaryCd)}
            </Typography>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#94a3b8", alignSelf: "center" }}>
              {primaryLeftLabel}
            </Typography>
            {primarySt != null && (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", ml: "auto" }}>
                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#475569", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                  {primaryFmt(primarySt)}
                </Typography>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 500, color: "#94a3b8" }}>
                  {primaryRightLabel}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Plain-English narrative */}
          {narrative && (
            <Typography sx={{
              fontSize: "0.67rem", color: "#475569", lineHeight: 1.45,
              borderTop: "1px solid #f1f5f9", pt: "6px",
            }}>
              {narrative}
            </Typography>
          )}

          {/* Wave delta badges — ONLY shown when multiple waves exist */}
          {hasWaveDelta && (
            <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "2px",
                px: "6px", py: "2px", borderRadius: "4px", fontSize: "0.61rem", fontWeight: 700,
                bgcolor: cdDelta >= 0 ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)",
                color: cdDelta >= 0 ? "#059669" : "#dc2626",
                border: `1px solid ${cdDelta >= 0 ? "rgba(5,150,105,0.18)" : "rgba(220,38,38,0.18)"}`,
              }}>
                {cdDelta >= 0 ? "▲" : "▼"} {Math.abs(cdDelta).toFixed(1)}pp district
              </Box>
              {stDelta != null && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "2px",
                  px: "6px", py: "2px", borderRadius: "4px", fontSize: "0.61rem", fontWeight: 600,
                  bgcolor: "rgba(148,163,184,0.08)", color: "#64748b",
                  border: "1px solid rgba(148,163,184,0.18)",
                }}>
                  {stDelta >= 0 ? "▲" : "▼"} {Math.abs(stDelta).toFixed(1)}pp state
                </Box>
              )}
              {curGap != null && Math.abs(curGap) >= 0.5 && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", ml: "auto",
                  px: "6px", py: "2px", borderRadius: "4px", fontSize: "0.61rem", fontWeight: 600,
                  bgcolor: "rgba(100,116,139,0.06)", color: "#475569",
                  border: "1px solid rgba(100,116,139,0.14)",
                }}>
                  {fmtPP(curGap)} vs state
                </Box>
              )}
            </Box>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", fontStyle: "italic" }}>
          No data available
        </Typography>
      )}
    </Box>
  );
}

function buildTopIssuesVariance({ cdItems = [], stateItems = [], districtName = "This district" }) {
  if (!cdItems.length || !stateItems.length) return null;
  const byKeyState = new Map(stateItems.map((d) => [d.key, d.value]));
  const diffs = cdItems.map((d) => ({
    label: d.label,
    diff: (d.value ?? 0) - (byKeyState.get(d.key) ?? 0),
  }));
  const sorted = diffs.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  const [a, b] = sorted;
  if (!a) return null;
  const fmt = (x) => `${x >= 0 ? "+" : ""}${x.toFixed(1)} pp`;
  if (b) {
    return `${districtName} over-indexes on ${a.label} (${fmt(a.diff)}) and under-indexes on ${b.label} (${fmt(b.diff)}), compared to the state.`;
  }
  return `${districtName} differs most on ${a.label} (${fmt(a.diff)}) compared to the state.`;
}

function buildTopIssuesCallout({ varianceDiffs, colorMap = {}, districtName }) {
  const pos = varianceDiffs?.positive;
  const neg = varianceDiffs?.negative;
  const fmt = (x) => `${x >= 0 ? "+" : ""}${x.toFixed(1)} pp`;
  if (!pos && !neg) return null;

  if (pos && neg) {
    return (
      <>
        {districtName} over-indexes on{" "}
        <span style={{ color: colorMap[pos.key] || "#1e88e5", fontWeight: 700 }}>
          {pos.label}
        </span>{" "}
        ({fmt(pos.diff)}) and under-indexes on{" "}
        <span style={{ color: colorMap[neg.key] || "#1e88e5", fontWeight: 700 }}>
          {neg.label}
        </span>{" "}
        ({fmt(neg.diff)}), compared to the state.
      </>
    );
  }

  const single = pos || neg;
  return (
    <>
      {districtName} differs most on{" "}
      <span style={{ color: colorMap[single.key] || "#1e88e5", fontWeight: 700 }}>
        {single.label}
      </span>{" "}
      ({fmt(single.diff)}), compared to the state.
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* PHOTO HELPER                                                                */
/* Photos served from unitedstates.github.io — same project as the JSON data */
/* Plain <img> loads work fine. Do NOT add crossOrigin="anonymous" — the host */
/* doesn't send CORS headers and the browser will show a broken image.        */
/* For PDF export via html2canvas, proxy through Django:                      */
/*   /api/img-proxy/?url=https://theunitedstates.io/images/congress/...      */
/* -------------------------------------------------------------------------- */

function bioguidePhotoUrl(bioguideId) {
  if (!bioguideId) return null;
  // GitHub Pages mirror — works as a plain <img src>, no CORS headers needed
  return `https://unitedstates.github.io/images/congress/225x275/${bioguideId}.jpg`;
}

/* -------------------------------------------------------------------------- */
/* DATA FETCHING                                                               */
/* All data from unitedstates/congress-legislators — no key, no CORS issues   */
/* -------------------------------------------------------------------------- */

const UNS_BASE = "https://unitedstates.github.io/congress-legislators";

// Module-level cache so we only fetch each file once per page load
let _membersCache = null;
let _committeesCache = null;
let _membershipCache = null;

async function fetchMembers() {
  if (_membersCache) return _membersCache;
  const res = await fetch(`${UNS_BASE}/legislators-current.json`);
  if (!res.ok) throw new Error(`legislators-current fetch ${res.status}`);
  _membersCache = await res.json();
  return _membersCache;
}

async function fetchCommitteeList() {
  if (_committeesCache) return _committeesCache;
  const res = await fetch(`${UNS_BASE}/committees-current.json`);
  if (!res.ok) throw new Error(`committees-current fetch ${res.status}`);
  _committeesCache = await res.json();
  return _committeesCache;
}

async function fetchCommitteeMembership() {
  if (_membershipCache) return _membershipCache;
  const res = await fetch(`${UNS_BASE}/committee-membership-current.json`);
  if (!res.ok) throw new Error(`committee-membership fetch ${res.status}`);
  _membershipCache = await res.json();
  return _membershipCache;
}

/**
 * Find the current House rep for a given state + district.
 *
 * legislators-current.json shape (per member):
 * {
 *   id: { bioguide, thomas, govtrack, ... },
 *   name: { first, last, official_full },
 *   bio: { birthday, gender },
 *   terms: [{ type, start, end, state, district, party, url, address, phone, ... }]
 * }
 *
 * The last term in the array is the current one.
 * type === "rep" for House members.
 */
async function findMember(stateAbbr, districtNum) {
  const district = Number(districtNum); // at-large states use 0
  const members = await fetchMembers();

  return members.find((m) => {
    const terms = m.terms || [];
    const latest = terms[terms.length - 1] || {};
    return (
      latest.type === "rep" &&
      latest.state === stateAbbr.toUpperCase() &&
      Number(latest.district) === district
    );
  }) || null;
}

/**
 * Get committee names for a member by their bioguide ID.
 *
 * committee-membership-current.json shape:
 * { "HSAP": [{ bioguide, name, rank, title, party }, ...], ... }
 *
 * committees-current.json has the committee names keyed by thomas_id / committee_id.
 */
// Standard abbreviations used by gov/public affairs professionals
// Keyed by the thomas_id from committees-current.json
const COMMITTEE_ABBR = {
  HSAG: "Agriculture",
  HSAP: "Appropriations",
  HSAS: "Armed Services",
  HSBA: "Financial Services",
  HSBU: "Budget",
  HSED: "Education & Workforce",
  HSEN: "Energy & Commerce",
  HSFA: "Foreign Affairs",
  HSHA: "House Admin",
  HSHM: "Homeland Security",
  HSIN: "Intelligence (HPSCI)",
  HSII: "Natural Resources",
  HSIF: "Energy & Commerce",
  HSJU: "Judiciary",
  HSLO: "House Admin",
  HSOG: "Oversight & Accountability",
  HSPW: "Transportation & Infrastructure",
  HSRU: "Rules",
  HSSC: "Science, Space & Technology",
  HSSM: "Small Business",
  HSVR: "Veterans' Affairs",
  HSWM: "Ways & Means",
  // Senate
  SSAF: "Agriculture (S)",
  SSAP: "Appropriations (S)",
  SSAS: "Armed Services (S)",
  SSBK: "Banking (S)",
  SSBU: "Budget (S)",
  SSCM: "Commerce (S)",
  SSEG: "Energy & Natural Resources (S)",
  SSEV: "Environment & Public Works (S)",
  SSFI: "Finance (S)",
  SSFR: "Foreign Relations (S)",
  SSGA: "Homeland Security & Gov Affairs (S)",
  SSHR: "HELP (S)",
  SSJU: "Judiciary (S)",
  SSRA: "Rules & Administration (S)",
  SSSB: "Small Business (S)",
  SSVA: "Veterans' Affairs (S)",
  // Select / special
  HSIG: "Intelligence (HPSCI)",
  HLIG: "Intelligence (HPSCI)",
};

// Role display labels — title field from committee-membership-current.json
const ROLE_LABELS = {
  "Chair": { label: "Chair", color: "#854F0B", bg: "#FAEEDA" },
  "Ranking Member": { label: "Ranking", color: "#185FA5", bg: "#E6F1FB" },
  "Vice Chair": { label: "Vice Chair", color: "#3B6D11", bg: "#EAF3DE" },
};

async function getMemberCommittees(bioguideId) {
  if (!bioguideId) return [];

  const [membership, committeeList] = await Promise.all([
    fetchCommitteeMembership(),
    fetchCommitteeList(),
  ]);

  // Build lookups: thomas_id → { name, abbr }
  const infoById = {};
  for (const c of committeeList) {
    if (c.thomas_id) {
      infoById[c.thomas_id] = {
        name: c.name,
        abbr: COMMITTEE_ABBR[c.thomas_id] || c.name,
      };
    }
    for (const sub of c.subcommittees || []) {
      const subId = `${c.thomas_id}${sub.thomas_id}`;
      infoById[subId] = {
        name: `${c.name} — ${sub.name}`,
        abbr: `${COMMITTEE_ABBR[c.thomas_id] || c.name}: ${sub.name}`,
        isSubcommittee: true,
      };
    }
  }

  const results = [];
  for (const [committeeId, members] of Object.entries(membership)) {
    const match = members.find((m) => m.bioguide === bioguideId);
    if (!match) continue;
    const info = infoById[committeeId] || { name: committeeId, abbr: committeeId };
    results.push({
      id: committeeId,
      name: info.name,
      abbr: info.abbr,
      isSubcommittee: info.isSubcommittee || false,
      rank: match.rank || null,
      title: match.title || null, // "Chair", "Ranking Member", "Vice Chair", etc.
    });
  }

  // Sort: titled roles first (Chair > Ranking > Vice Chair), then by rank
  return results.sort((a, b) => {
    const aTitle = a.title ? 0 : 1;
    const bTitle = b.title ? 0 : 1;
    if (aTitle !== bTitle) return aTitle - bTitle;
    return (a.rank || 99) - (b.rank || 99);
  });
}

/**
 * Ideology score — Voteview blocks browser CORS requests so this is disabled.
 * To re-enable, add a Django proxy view:
 *
 *   def voteview_proxy(request):
 *       bioguide = request.GET.get("bioguide", "")
 *       r = requests.get(f"https://voteview.com/api/search?q=bioguide_id:{bioguide}&type=members")
 *       return JsonResponse(r.json(), safe=False)
 *
 * Then change the fetch below to /api/voteview-proxy/?bioguide={bioguideId}
 */
async function fetchIdeologyScore(_bioguideId) {
  return null;
}

/**
 * Master loader — returns a flat repData object for the top card.
 */
export async function loadFullRepData(stateAbbr, districtNum) {
  if (!stateAbbr || districtNum == null) return null;

  const district = Number(districtNum);
  const member = await findMember(stateAbbr, district);

  if (!member) {
    console.warn(`loadFullRepData: no member found for ${stateAbbr}-${district}`);
    return null;
  }

  const bioguideId = member.id?.bioguide || null;
  const terms = member.terms || [];
  const latestTerm = terms[terms.length - 1] || {};

  // Serving since = start year of their first House term
  const houseTerms = terms.filter((t) => t.type === "rep");
  const servingSince = houseTerms.length
    ? Number((houseTerms[0].start || "").slice(0, 4)) || null
    : null;

  const committees = await getMemberCommittees(bioguideId);

  return {
    bioguideId,
    fullName: member.name?.official_full || `${member.name?.first} ${member.name?.last}`,
    currentParty: latestTerm.party || null,
    state: latestTerm.state || stateAbbr,
    district: latestTerm.district ?? districtNum,
    servingSince,
    officialWebsite: latestTerm.url || null,
    officeAddress: latestTerm.address || null,
    phone: latestTerm.phone || null,
    photoUrl: bioguidePhotoUrl(bioguideId),
    committees,
  };
}


/* -------------------------------------------------------------------------- */
/* ELECTION RESULT PILLS  (mirrors SlimTag)                                   */
/* -------------------------------------------------------------------------- */

function USSlimTag({ label, party, share, margin, logoUrl }) {
  const partyNorm = normalizeUSParty(party);
  const baseColor = usPartyColor(partyNorm) || "#999";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.6,
        borderRadius: "999px",
        bgcolor: alpha(baseColor, 0.08),
        border: `1px solid ${alpha(baseColor, 0.15)}`,
        flexShrink: 0,
        flexGrow: 1,
      }}
    >
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt=""
          sx={{ height: 14, width: "auto", opacity: 0.9 }}
        />
      ) : (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: baseColor,
            boxShadow: `0 0 0 2px ${alpha(baseColor, 0.3)}`,
          }}
        />
      )}

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, flexWrap: "wrap" }}>
        <Typography
          component="span"
          sx={{
            fontSize: "clamp(0.65rem, 1vw, 0.7rem)",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>

        <Typography
          component="span"
          sx={{ fontSize: "clamp(0.75rem, 1.1vw, 0.8rem)", fontWeight: 800, color: "text.primary" }}
        >
          {partyNorm ?? "—"}
        </Typography>

        {(Number.isFinite(share) || Number.isFinite(margin)) && (
          <Box component="span" sx={{ display: "inline-flex", gap: 0.5, alignItems: "baseline" }}>
            {Number.isFinite(share) && (
              <Typography
                component="span"
                sx={{ fontSize: "clamp(0.7rem, 1vw, 0.75rem)", fontWeight: 500, color: "text.primary" }}
              >
                {Number(share).toFixed(1)}%
              </Typography>
            )}
            {Number.isFinite(margin) && (
              <>
                <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
                  •
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: "clamp(0.7rem, 1vw, 0.75rem)", color: "text.secondary" }}
                >
                  Margin {Number(margin).toFixed(1)}pp
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* TOP HEADER                                                                  */
/* -------------------------------------------------------------------------- */

export function DistrictTopHeader({ districtName, state, district, repData, topVI, viRow }) {
  const stackLogo = process.env.PUBLIC_URL + "/static/images/stack_logo.png";

  const currentParty = repData?.currentParty || null;

  const projParty = viRow?.winner
    ? normalizeUSParty(viRow.winner)
    : topVI?.label ?? null;

  const normWinner = viRow?.winner ? normalizeUSParty(viRow.winner) : null;
  const demN = viRow?.dem_n != null ? Number(viRow.dem_n) : null;
  const repN = viRow?.rep_n != null ? Number(viRow.rep_n) : null;
  const totalN = (demN ?? 0) + (repN ?? 0);
  const winnerShare = totalN > 0
    ? normWinner === "Democrat" && demN != null ? demN / totalN
    : normWinner === "Republican" && repN != null ? repN / totalN
    : null
    : null;

  const projShare = winnerShare != null
    ? winnerShare * 100
    : Number.isFinite(+topVI?.district) ? +topVI.district : undefined;

  const accent = currentParty
    ? usPartyColor(currentParty)
    : projParty
    ? usPartyColor(projParty)
    : "#5FE1E6";

  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <Box sx={{ mb: "10px", pb: "10px", borderBottom: "2px solid #f1f5f9" }}>
      {/* Brand row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box component="img" src={stackLogo} alt="Stack Data Strategy" sx={{ height: 16, opacity: 0.7 }} />
          <Box sx={{ width: "1px", height: "12px", bgcolor: "#e2e8f0" }} />
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            District Profile
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.60rem", color: "#b0bac8" }}>
          Stack Data Strategy · {today}
        </Typography>
      </Box>

      {/* District name row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
        <Box>
          <Typography sx={{
            fontSize: "1.55rem", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1,
            color: "#0f172a", wordBreak: "break-word",
          }}>
            {districtName || `${state}-${district}`}
          </Typography>
          <Box sx={{ mt: "5px", height: "3px", width: "48px", borderRadius: "2px", background: `linear-gradient(90deg, ${accent}, ${accent}44)` }} />
        </Box>

        {/* Pills */}
        <Box sx={{ display: "flex", flexDirection: "row", gap: "6px", flexShrink: 0, alignItems: "center", mt: "2px" }}>
          {projParty && (
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              px: "10px", py: "5px", borderRadius: "6px",
              bgcolor: alpha(usPartyColor(projParty), 0.07),
              border: `1px solid ${alpha(usPartyColor(projParty), 0.18)}`,
            }}>
              <Box component="img" src={stackLogo} alt="" sx={{ height: 12, opacity: 0.6 }} />
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Projection
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: usPartyColor(projParty) }}>
                {projParty}{projShare != null ? ` ${projShare.toFixed(1)}%` : ""}
              </Typography>
            </Box>
          )}
          {currentParty && (
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              px: "10px", py: "5px", borderRadius: "6px",
              bgcolor: alpha(usPartyColor(currentParty), 0.07),
              border: `1px solid ${alpha(usPartyColor(currentParty), 0.18)}`,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: usPartyColor(currentParty) }} />
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Current Rep.
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: usPartyColor(currentParty) }}>
                {normalizeUSParty(currentParty)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* PHOTO WITH INITIALS FALLBACK                                                */
/* theunitedstates.io photos load fine in browsers — crossOrigin is needed    */
/* only if you later use html2canvas for PDF export (avoids canvas taint).    */
/* -------------------------------------------------------------------------- */

function PhotoWithFallback({ src, alt, initials, color }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <Box
        sx={{
          width: 76, height: 96, borderRadius: 1.5,
          bgcolor: `${color}22`,
          border: `1px solid ${color}55`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, fontWeight: 700, color,
          letterSpacing: -1,
        }}
      >
        {initials}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt || "Rep photo"}
      onError={() => setFailed(true)}
      sx={{
        width: 76, height: 96, objectFit: "cover",
        borderRadius: 1.5, border: `1px solid ${color}55`,
        display: "block",
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* REP CARD                                                                    */
/* -------------------------------------------------------------------------- */

export function RepCard({ repData }) {
  if (!repData) {
    return (
      <Box sx={{ borderRadius: "8px", p: "12px", mb: "8px", height: 110, bgcolor: "#f8fafc", border: "1px solid #e8edf4" }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>Loading representative information…</Typography>
      </Box>
    );
  }

  const { fullName, currentParty, state, district, servingSince, officialWebsite, photoUrl, committees } = repData;

  const partyNorm     = normalizeUSParty(currentParty);
  const repPartyColor = usPartyColor(partyNorm);
  const districtLabel = district != null ? `${state}-${district}` : state;
  const yearsServing  = servingSince ? new Date().getFullYear() - servingSince : null;
  const shownCommittees = (committees || []).slice(0, 4);

  return (
    <Box sx={{
      borderRadius: "8px",
      border: "1px solid #e8edf4",
      bgcolor: "#fff",
      mb: "8px",
      overflow: "hidden",
      display: "flex",
      boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
    }}>
      <Box sx={{ width: "4px", flexShrink: 0, bgcolor: repPartyColor }} />
      <Box sx={{ display: "flex", flex: 1, p: "12px 14px", gap: "14px", alignItems: "stretch", minWidth: 0 }}>
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "flex-start" }}>
          <PhotoWithFallback
            src={photoUrl}
            alt={fullName}
            initials={(fullName || "").split(" ").map((w) => w[0]).join("").slice(0, 2)}
            color={repPartyColor}
          />
        </Box>
        <Box sx={{ flex: "0 0 auto", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {fullName}
            </Typography>
            <Box sx={{ px: "6px", py: "2px", borderRadius: "4px", bgcolor: alpha(repPartyColor, 0.10), border: `1px solid ${alpha(repPartyColor, 0.22)}` }}>
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: repPartyColor, letterSpacing: "0.05em" }}>{partyNorm}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: "0.70rem", color: "#64748b", fontWeight: 500 }}>
            Representative for {districtLabel}
            {yearsServing != null && (
              <span style={{ color: "#94a3b8", marginLeft: "6px" }}>· Serving since {servingSince} ({yearsServing}y)</span>
            )}
          </Typography>
          {officialWebsite && (
            <Typography component="a" href={officialWebsite} target="_blank" rel="noopener noreferrer"
              sx={{ fontSize: "0.65rem", color: "#3b82f6", fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
              🌐 {officialWebsite.replace(/^https?:\/\//, "")}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: "1px", bgcolor: "#f1f5f9", flexShrink: 0, mx: "4px" }} />
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "5px" }}>
          <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.10em", color: "#94a3b8", mb: "2px" }}>
            Committee Assignments
          </Typography>
          {shownCommittees.length > 0 ? (
            shownCommittees.map((c, i) => {
              const roleInfo = c.title ? ROLE_LABELS[c.title] : null;
              return (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {c.isSubcommittee && <Typography sx={{ fontSize: "0.60rem", color: "#cbd5e1", ml: "8px" }}>↳</Typography>}
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: c.title ? 700 : 500, color: c.isSubcommittee ? "#64748b" : "#334155", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.abbr || c.name}
                  </Typography>
                  {c.rank && !roleInfo && <Typography sx={{ fontSize: "0.60rem", color: "#94a3b8", flexShrink: 0 }}>#{c.rank}</Typography>}
                  {roleInfo && (
                    <Box sx={{ px: "5px", py: "1px", borderRadius: "3px", bgcolor: roleInfo.bg, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: roleInfo.color }}>{roleInfo.label}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", fontStyle: "italic" }}>No committee data</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* DISTRICT DEMOGRAPHIC BLOCK                                                  */
/* -------------------------------------------------------------------------- */

// US national baselines — ACS 5-year 2022 approximations
// These are fallback constants; swap for a live national fetch if available
export const US_NATIONAL = {
  median_age:                              39,
  median_income:                           78538,
  education_degreeplus:                    0.44,
  poverty_below_poverty_line:              0.12,
  healthcare_reliant_on_medicaid_medicare: 0.21,
  tenure_own_home:                         0.63,
};

function buildDistrictBlurb(cd, state, districtName, stateName, benchLabel = "the state") {
  if (!cd || !state) return null;
  const bench = benchLabel;

  // Fields where higher = notable (income, education, homeownership)
  // Fields where lower = notable (poverty)
  const checks = [
    { key: "median_income",           label: "higher income",      higherIsBetter: true  },
    { key: "education_degreeplus",    label: "more college-educated", higherIsBetter: true },
    { key: "tenure_own_home",         label: "more homeowners",    higherIsBetter: true  },
    { key: "poverty_below_poverty_line", label: "higher poverty",  higherIsBetter: false },
    { key: "healthcare_reliant_on_medicaid_medicare", label: "more Medicare/Medicaid reliance", higherIsBetter: false },
  ];

  const diffs = checks
    .map(({ key, label }) => {
      const cdv = Number(cd[key]);
      const stv = Number(state[key]);
      if (isNaN(cdv) || isNaN(stv) || stv === 0) return null;
      // Use relative % for income/age, absolute pp for fractions
      const isAbsolute = cdv <= 1;
      const diff = isAbsolute ? (cdv - stv) * 100 : cdv - stv;
      const threshold = isAbsolute ? 2 : (key === "median_income" ? 3000 : 1);
      if (Math.abs(diff) < threshold) return null;
      return { label, diff };
    })
    .filter(Boolean);

  const higher = diffs.filter((d) => d.diff > 0).sort((a, b) => b.diff - a.diff).slice(0, 2);
  const lower  = diffs.filter((d) => d.diff < 0).sort((a, b) => a.diff - b.diff).slice(0, 1);

  if (!higher.length && !lower.length) {
    return `${districtName} closely mirrors ${bench} demographically.`;
  }

  const parts = [];
  if (higher.length) parts.push(`${higher.map((d) => d.label).join(" and ")} than ${bench}`);
  if (lower.length)  parts.push(`lower ${lower.map((d) => d.label.replace(/^(higher|more) /, "")).join(" and ")}`);
  return `${districtName} has ${parts.join(", and ")}.`;
}

export function DistrictDemographicBlock({ demoRow: cd, demoBenchmark: st, districtName, stateName, demoLoading, benchLabel, areaLabel = "CD" }) {
  if (demoLoading) {
    return (
      <Box sx={{ py: 1.5, display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled", fontSize: "0.85rem" }}>
        Loading demographic profile…
      </Box>
    );
  }

  if (!cd && !st) return null;

  const blurb = buildDistrictBlurb(cd, st, districtName, stateName, benchLabel);

  // Raw-number stat tile — same card style as Field but shows actual values not %
  const RawStatTile = ({
    label,
    cdVal,
    stVal,
    usVal,
    icon,
    iconColor,
    format,
    deltaThreshold = 0.5,
    valueType = "percent", // "percent" | "age" | "currency"
  }) => {
    const fmt = (v) => v != null && !isNaN(Number(v)) ? format(Number(v)) : "—";
    const cdNum = cdVal != null ? Number(cdVal) : null;
    const stNum = stVal != null ? Number(stVal) : null;
    const delta = cdNum != null && stNum != null ? cdNum - stNum : null;
    const isUp = delta != null && delta > 0;
    // gauge: scale to 0-1 relative to a sensible max
    let gaugeVal = null;
    let gaugeNat = null;
    if (valueType === "percent") {
      gaugeVal = cdNum != null ? Math.min(cdNum / 100, 1) : null;
      gaugeNat = stNum != null ? Math.min(stNum / 100, 1) : null;
    } else if (valueType === "age") {
      const gaugeMax = 60;
      gaugeVal = cdNum != null ? Math.min(cdNum / gaugeMax, 1) : null;
      gaugeNat = stNum != null ? Math.min(stNum / gaugeMax, 1) : null;
    } else if (valueType === "currency") {
      const gaugeMax = 150000;
      gaugeVal = cdNum != null ? Math.min(cdNum / gaugeMax, 1) : null;
      gaugeNat = stNum != null ? Math.min(stNum / gaugeMax, 1) : null;
    }

    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{
          display: "flex", flexDirection: "column", gap: 0.55,
          px: 0.75, py: 0.75, borderRadius: 1.5,
          background: "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(248,250,252,0.85))",
          border: "1px solid rgba(148,163,184,0.30)",
          boxShadow: "0 4px 14px rgba(15,23,42,0.06)",
        }}>
          {/* Icon + label */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: "50%",
              backgroundColor: iconColor, color: "white",
              display: "grid", placeItems: "center", flexShrink: 0,
            }}>
              {icon}
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {label}
            </Typography>
          </Box>
          {/* Values */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4, flexWrap: "nowrap" }}>
            <Typography variant="body2" sx={{
              fontWeight: 800,
              color: delta != null && Math.abs(delta) > deltaThreshold
                ? (isUp ? "success.dark" : "error.dark")
                : "text.primary",
            }}>
             
              {fmt(cdVal)}
            </Typography>
            {stNum != null && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                ({fmt(stVal)})
              </Typography>
            )}
          </Box>
          {/* Mini gauge */}
          {gaugeVal != null && (
            <Box sx={{ position: "relative", height: 6, borderRadius: 999, background: "linear-gradient(90deg, rgba(148,163,184,0.18), rgba(148,163,184,0.12))", overflow: "hidden" }}>
              <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${gaugeVal * 100}%`, background: "linear-gradient(90deg, rgba(95,225,230,0.85), rgba(95,225,230,0.55))" }} />
              {gaugeNat != null && (
                <Box sx={{ position: "absolute", left: `calc(${gaugeNat * 100}% - 1px)`, top: -2, bottom: -2, width: 2, backgroundColor: "rgba(15,23,42,0.55)", borderRadius: 1 }} />
              )}
            </Box>
          )}
        </Box>
        {/* National reference */}
        {usVal != null && (
          <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "text.disabled", display: "block", textAlign: "center", mt: 0.3 }}>
            US: {format(usVal)}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ml: 0, mr: 0, py: 1,
        width: "100%",
        borderTop: "1px solid rgba(148,163,184,0.25)",
        borderBottom: "1px solid rgba(148,163,184,0.25)",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.2, px: 0.4, py: 0.4 }}>
        <Typography
          variant="subtitle1" fontWeight={700}
          sx={{ color: "text.primary", borderRight: "2px solid #5FE1E6", pr: 1, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Demographic Profile
        </Typography>
        {blurb && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary", lineHeight: 1.25, fontSize: "0.8rem", flex: 1, minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            }}
            title={blurb}
          >
            {blurb}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "text.disabled", whiteSpace: "nowrap", flexShrink: 0 }}>
          {areaLabel}&nbsp;({stateName || "state"})&nbsp;<span style={{ opacity: 0.6 }}>US</span>
        </Typography>
      </Box>

      {/* Field row — scaled to fit all 6 cards at report width */}
      <Box sx={{ fontSize: "0.88em" }}>
      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 0.75 }}>

        {/* Median age — raw number */}
        <RawStatTile
          label="Median age"
          cdVal={cd?.median_age}
          stVal={st?.median_age}
          usVal={US_NATIONAL.median_age}
          icon={<ElderlyIcon sx={{ fontSize: 14 }} />}
          iconColor="#a855f7"
          format={(v) => v.toFixed(1)}
          valueType="age"
        />

        {/* Median income — dollar amount */}
        <RawStatTile
          label="Median income"
          cdVal={cd?.median_income}
          stVal={st?.median_income}
          usVal={US_NATIONAL.median_income}
          icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
          iconColor="#16a34a"
          format={(v) => `$${Math.round(v).toLocaleString()}`}
          valueType="currency"
        />

        {/* College degree+ */}
        <RawStatTile
          label="College+"
          cdVal={cd?.education_degreeplus != null ? cd.education_degreeplus * 100 : null}
          stVal={st?.education_degreeplus  != null ? st.education_degreeplus  * 100 : null}
          usVal={US_NATIONAL.education_degreeplus * 100}
          icon={<SchoolIcon sx={{ fontSize: 14 }} />}
          iconColor="#2563eb"
          format={(v) => `${v.toFixed(0)}%`}
          deltaThreshold={1}
          valueType="percent"
        />

        {/* In poverty */}
        <RawStatTile
          label="In poverty"
          cdVal={cd?.poverty_below_poverty_line != null ? cd.poverty_below_poverty_line * 100 : null}
          stVal={st?.poverty_below_poverty_line  != null ? st.poverty_below_poverty_line  * 100 : null}
          usVal={US_NATIONAL.poverty_below_poverty_line * 100}
          icon={<MoneyOffIcon sx={{ fontSize: 14 }} />}
          iconColor="#9ca3af"
          format={(v) => `${v.toFixed(0)}%`}
          deltaThreshold={1}
          valueType="percent"
        />

        {/* Medicare/Medicaid */}
        <RawStatTile
          label="Medicare/aid"
          cdVal={cd?.healthcare_reliant_on_medicaid_medicare != null ? cd.healthcare_reliant_on_medicaid_medicare * 100 : null}
          stVal={st?.healthcare_reliant_on_medicaid_medicare  != null ? st.healthcare_reliant_on_medicaid_medicare  * 100 : null}
          usVal={US_NATIONAL.healthcare_reliant_on_medicaid_medicare * 100}
          icon={<LocalHospitalIcon sx={{ fontSize: 14 }} />}
          iconColor="#f59e0b"
          format={(v) => `${v.toFixed(0)}%`}
          deltaThreshold={1}
          valueType="percent"
        />

        {/* Homeowners */}
        <RawStatTile
          label="Homeowners"
          cdVal={cd?.tenure_own_home != null ? cd.tenure_own_home * 100 : null}
          stVal={st?.tenure_own_home  != null ? st.tenure_own_home  * 100 : null}
          usVal={US_NATIONAL.tenure_own_home * 100}
          icon={<HomeIcon sx={{ fontSize: 14 }} />}
          iconColor="#ef4444"
          format={(v) => `${v.toFixed(0)}%`}
          deltaThreshold={1}
          valueType="percent"
        />

      </Box>
      </Box>
    </Box>
  );
}


/* -------------------------------------------------------------------------- */
/* BAR CHART AUTO-HEIGHT WRAPPER                                               */
/* Measures its flex container and passes the pixel height into BarCompare    */
/* -------------------------------------------------------------------------- */

function BarChartAutoHeight({ data, districtName, stateName, varianceDiffs, varianceText, colorMap }) {
  const containerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(320);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setChartHeight(Math.floor(h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, position: "relative" }}>
      <BarCompareConstNat
        data={data}
        areaName={districtName}
        stateName={stateName}
        height={chartHeight}
        showNational={false}
      />
      {(varianceDiffs || varianceText) && (
        <Box sx={{
          position: "absolute", bottom: 14, right: 0,
          maxWidth: 150, bgcolor: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: "6px", p: "7px 9px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        }}>
          <Typography sx={{ fontSize: "0.70rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", mb: "3px" }}>
            Key Difference
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "#78350f", lineHeight: 1.4 }}>
            {varianceDiffs
              ? buildTopIssuesCallout({ varianceDiffs, colorMap, districtName })
              : varianceText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* MAIN VIEW                                                                   */
/* -------------------------------------------------------------------------- */

const DistrictReportView = ({
  id,               // forwarded from ReportPage for html2canvas targeting
  districtName,     // e.g. "North Carolina's 12th District"
  state,            // e.g. "NC"
  district,         // e.g. 12
  areaCode,         // your internal area code for opinion data lookups
  stateFips,
  stateName,
  onReady,          // optional: called once the report's data has finished loading
                    // (used by the standalone render route to gate PDF capture)
}) => {
  /* ── Opinion data (same pattern as ConstituencyReportView) ── */
  const [charts, setCharts] = useState({
    vi: [],
    national: [],
    priorities: [],
    best_pm: [],
    loading: true,
  });

  const topVI = getTopVI(charts.vi);
  const prevArea = useRef(null);

  useEffect(() => {
    if (!areaCode || areaCode === prevArea.current) return;
    prevArea.current = areaCode;

    setCharts({ vi: [], national: [], priorities: [], best_pm: [], loading: true });

    (async () => {
      try {
        const [vi, national, priorities, best_pm] = await Promise.all([
          loadGroupData({ groupId: "vote_intention", areaCode }),
          loadGroupData({ groupId: "national_issues", areaCode }),
          loadGroupData({ groupId: "priorities", areaCode }),
          loadGroupData({ groupId: "best_pm", areaCode }),
        ]);
        setCharts({ vi, national, priorities, best_pm, loading: false });
      } catch {
        setCharts((s) => ({ ...s, loading: false }));
      }
    })();
  }, [areaCode]);

  /* ── Top Issues (tracking: latest wave) ── */
  const [topIssues, setTopIssues] = useState({
    loading: true,
    latestWave: null,
    data: [],
    stateTop: [],
    cdTop: [],
    varianceText: null,
    varianceDiffs: null,
    colorMap: {},
  });
  const [keyChanges, setKeyChanges] = useState({
    loading: true,
    items: [],
  });

  useEffect(() => {
    if (!stateFips || district == null) return;
    let isActive = true;

    const fetchLevelRows = async (level, stateCodes = [], wave = null, extraKeys = []) => {
      const { groupsById } = getPolConfig();
      const group = groupsById?.pol_top_issues;
      if (!group) return [];
      const res = await fetch(`/api/${level}_data_arrow/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          theme: "pol",
          groupKeys: [...group.keys, ...extraKeys],
          stateCodes,
          schema: "tracking",
          schema_type: "tracking",
          wave,
        }),
      });
      if (!res.ok) throw new Error(`top issues ${level} ${res.status}`);
      const buf = await res.arrayBuffer();
      const rows = rowsFromTable(tableFromIPC(buf));
      return rows || [];
    };

    const buildTop = (row, group) => {
      if (!row || !group?.keys?.length) return [];
      return group.keys
        .map((k) => ({
          key: k,
          label: group.labels?.[k] || k,
          value: Number(row?.[k]),
        }))
        .filter((d) => Number.isFinite(d.value))
        .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
        .slice(0, 5);
    };

    const pickLatestWave = () => 3;

    const normalizeDigits = (v) => String(v ?? "").replace(/\D/g, "");

    const findStateRow = (rows, fips) => {
      const f = String(fips).padStart(2, "0");
      return (
        rows.find((r) => String(r?.state_fips || r?.state || "").padStart(2, "0") === f) ||
        rows[0] ||
        null
      );
    };

    const findCdRow = (rows, fips, distNum) => {
      const f = String(fips).padStart(2, "0");
      const distRaw = normalizeDigits(distNum);
      const distVariants = new Set(
        [distRaw, distRaw.padStart(2, "0"), distRaw.padStart(3, "0"), distRaw.padStart(4, "0"), distRaw.padStart(5, "0")]
          .filter(Boolean)
      );
      const areaCandidates = new Set(
        Array.from(distVariants).map((d) => `${f}${d}`)
      );

      return (
        rows.find((r) => {
          const rState = String(r?.state_fips || r?.state || "").padStart(2, "0");
          const rDist = normalizeDigits(r?.dist_fips || r?.district);
          const rArea = normalizeDigits(r?.area_id);
          const stateOk = rState ? rState === f : true;
          const distOk = rDist ? distVariants.has(rDist) : false;
          const areaOk = rArea ? (areaCandidates.has(rArea) || distVariants.has(rArea)) : false;
          return stateOk && (distOk || areaOk);
        }) || null
      );
    };

    const computeWeightedAvgRow = (rows, keys) => {
      if (!rows?.length) return null;
      const avg = {};
      const weights = rows.map((r) => Number(r?.total_sum)).map((w) => (Number.isFinite(w) ? w : 0));
      const totalW = weights.reduce((a, b) => a + b, 0);
      keys.forEach((k) => {
        let num = 0;
        for (let i = 0; i < rows.length; i++) {
          const v = Number(rows[i]?.[k]);
          const w = weights[i];
          if (Number.isFinite(v) && Number.isFinite(w)) num += v * w;
        }
        avg[k] = totalW ? num / totalW : null;
      });
      return avg;
    };

    (async () => {
      try {
        setTopIssues((s) => ({ ...s, loading: true }));
        const { groupsById } = getPolConfig();
        const group = groupsById?.pol_top_issues;
        if (!group) throw new Error("pol_top_issues not found");

        const natAll = await fetchLevelRows("national", [], null);
        const latestWave = pickLatestWave(natAll);
        const natRows = latestWave != null ? await fetchLevelRows("national", [], latestWave) : natAll;
        const stateRows = await fetchLevelRows("states", [String(stateFips)], latestWave);
        const cdRows = await fetchLevelRows("cd", [String(stateFips)], latestWave);

        const natRow = natRows?.[0] || null;
        const stateRow = findStateRow(stateRows, stateFips);
        const cdRow = findCdRow(cdRows, stateFips, district);

        const natFallback = !natRow ? computeWeightedAvgRow(stateRows, group.keys) : null;
        const resolvedNatRow = natRow || natFallback;

        console.log("[top_issues] rows", {
          latestWave,
          natCount: natRows?.length || 0,
          stateCount: stateRows?.length || 0,
          cdCount: cdRows?.length || 0,
          natSample: natRows?.[0],
          stateSample: stateRows?.[0],
          cdSample: cdRows?.[0],
        });
        const nonNullCdCounts = (group.keys || []).reduce((acc, k) => {
          acc[k] = (cdRows || []).reduce((n, r) => (Number.isFinite(Number(r?.[k])) ? n + 1 : n), 0);
          return acc;
        }, {});
        console.log("[top_issues] cd non-null counts (first 5)", Object.entries(nonNullCdCounts).slice(0, 5));
        console.log("[top_issues] resolved", {
          stateFips,
          district,
          natRow: resolvedNatRow,
          stateRow,
          cdRow,
        });

        const distCodeRaw = String(district ?? "").trim().toUpperCase();
        const isAtLarge = distCodeRaw === "AL" || String(district).padStart(2, "0") === "00";
        const data = (group.keys || []).map((k) => ({
          key: k,
          label: group.labels?.[k] || k,
          district: isAtLarge && stateRow ? Number(stateRow?.[k]) : cdRow ? Number(cdRow?.[k]) : null,
          state: stateRow ? Number(stateRow?.[k]) : null,
          national: resolvedNatRow ? Number(resolvedNatRow?.[k]) : null,
          color: group.colors?.[k] || "#888",
        }));

        console.log("[top_issues] data sample", data.slice(0, 5));
        console.log("[top_issues] nonzero counts", {
          district: data.filter((d) => Number.isFinite(d.district) && d.district !== 0).length,
          state: data.filter((d) => Number.isFinite(d.state) && d.state !== 0).length,
          national: data.filter((d) => Number.isFinite(d.national) && d.national !== 0).length,
        });

        const hasAnyValue = (row) =>
          row && group.keys.some((k) => Number.isFinite(Number(row?.[k])) && Number(row?.[k]) !== 0);
        const defaultRow = hasAnyValue(stateRow) ? stateRow : resolvedNatRow || null;
        const dataWithFallback = data.map((d) => ({
          ...d,
          district:
            Number.isFinite(d.district) && d.district !== 0
              ? d.district
              : Number.isFinite(defaultRow?.[d.key])
              ? Number(defaultRow[d.key])
              : d.district,
        }));

        const stateTop = buildTop(stateRow, group);
        const cdTop = buildTop(cdRow, group);
        const varianceText = buildTopIssuesVariance({
          cdItems: cdTop,
          stateItems: stateTop,
          districtName,
        });
        const varianceDiffs = (() => {
          if (!cdTop.length || !stateTop.length) return null;
          const byKeyState = new Map(stateTop.map((d) => [d.key, d.value]));
          const diffs = cdTop.map((d) => ({
            key: d.key,
            label: d.label,
            diff: (d.value ?? 0) - (byKeyState.get(d.key) ?? 0),
          }));
          const positive = diffs.filter((d) => d.diff > 0.5).sort((a, b) => b.diff - a.diff)[0] || null;
          const negative = diffs.filter((d) => d.diff < -0.5).sort((a, b) => a.diff - b.diff)[0] || null;
          return { positive, negative };
        })();

        if (!isActive) return;
        setTopIssues({
          loading: false,
          latestWave,
          data: dataWithFallback,
          stateTop,
          cdTop,
          varianceText,
          varianceDiffs,
          colorMap: group.colors || {},
        });

        // ── Key changes: 2 batched requests instead of 6 ──
        setKeyChanges((s) => ({ ...s, loading: true }));

        const immGroup    = groupsById?.immig_attitudes;
        const econGroup   = groupsById?.econ_approve;
        const issGroup    = groupsById?.pol_top_issues;
        const incomeGroup = groupsById?.pol_support_bbb;

        // Combine all keys from all groups into one request per level
        const allTrackingKeys = [
          ...(immGroup?.keys    || []),
          ...(econGroup?.keys   || []),
          ...(issGroup?.keys    || []),
          ...(incomeGroup?.keys || []),
        ];

        const fetchBatched = async (level, stateCodes) => {
          if (!allTrackingKeys.length) return [];
          const res = await fetch(`/api/${level}_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ theme: "pol", groupKeys: allTrackingKeys, stateCodes, schema: "tracking", schema_type: "tracking" }),
          });
          if (!res.ok) return [];
          const buf = await res.arrayBuffer();
          return rowsFromTable(tableFromIPC(buf)) || [];
        };

        const fipsStr = [String(stateFips)];

        const [batchedStateSeries, batchedCdSeries] = await Promise.all([
          fetchBatched("states", fipsStr),
          fetchBatched("cd",     fipsStr),
        ]);

        const toWaveNum = (r) => Number(r?.wave);
        const byWave = (rows) =>
          rows.map((r) => ({ ...r, _wave: toWaveNum(r) })).filter((r) => Number.isFinite(r._wave));

        // State rows: match by area_id (2-digit fips) or state_fips
        const filterState = (rows) => {
          const target = String(stateFips).padStart(2, "0");
          return rows.filter((r) =>
            String(r?.area_id    || "").padStart(2, "0") === target ||
            String(r?.state_fips || "").padStart(2, "0") === target
          );
        };

        // CD rows: match by area_id (4-digit) or district string e.g. "CT-02"
        const filterCd = (rows) => {
          const targetFips   = String(stateFips).padStart(2, "0");
          const distPadded   = String(district || "").padStart(2, "0");
          const targetAreaId = `${targetFips}${distPadded}`;
          return rows.filter((r) => {
            if (r?.area_id && String(r.area_id) === targetAreaId) return true;
            if (r?.district) {
              const parts = String(r.district).split("-");
              const dNum  = parts[1] ? String(Number(parts[1])).padStart(2, "0") : "";
              if (dNum === distPadded && String(r?.state_fips || "").padStart(2, "0") === targetFips) return true;
            }
            return false;
          });
        };

        const sorted = (rows) => byWave(rows).sort((a, b) => a._wave - b._wave);

        // All series share the same batched rows — just filtered differently
        const stSorted = sorted(filterState(batchedStateSeries));
        const cdSorted = isAtLarge ? stSorted : sorted(filterCd(batchedCdSeries));

        // Reuse for issues/immig/econ — same rows, different keys
        const immStSorted  = stSorted;
        const immCdSorted  = cdSorted;
        const econStSorted = stSorted;
        const econCdSorted = cdSorted;
        const issStSorted  = stSorted;
        const issCdSorted  = cdSorted;

        // Rows that carry a REAL value for this key, wave-ascending.
        //
        // The explicit null check matters: Number(null) is 0 and
        // Number.isFinite(0) is true, so a row whose value is null is
        // indistinguishable from a legitimate 0.0% unless it is screened out
        // here. curVal used to omit this check while waveDelta included it,
        // which is why every callout rendered "0.0%" for the level while the
        // deltas beside them were populated from the real rows.
        const rowsWithVal = (arr, key) => arr.filter((r) => {
          const raw = r?.[key];
          if (raw == null) return false;
          const v = Number(raw);
          return Number.isFinite(v) && Math.abs(v) > 1e-9;
        });

        // Latest wave against the one before it. This used to span the FIRST
        // valued wave to the last, i.e. cumulative movement across the whole
        // tracker, while the section caption reads "Δ vs. prior wave" — hence
        // the Math.abs(cdDelta) <= 20 sanity cap further down, which was
        // suppressing implausible spans rather than fixing them.
        const waveDelta = (arr, key) => {
          const withVal = rowsWithVal(arr, key);
          if (withVal.length < 2) return null;
          const prev = Number(withVal[withVal.length - 2][key]);
          const last = Number(withVal[withVal.length - 1][key]);
          return Number.isFinite(prev) && Number.isFinite(last) ? last - prev : null;
        };

        const curVal = (arr, key) => {
          const withVal = rowsWithVal(arr, key);
          const l = withVal[withVal.length - 1];
          const v = Number(l?.[key]);
          return Number.isFinite(v) ? v : null;
        };

        // ── Immigration attitudes (agree with admin) ──
        const immAgreeKey = "pol_policy:immig_agree:lik";
        const immCdDelta = waveDelta(immCdSorted, immAgreeKey);
        const immStDelta = waveDelta(immStSorted, immAgreeKey);
        const immCdCur   = curVal(immCdSorted, immAgreeKey);
        const immStCur   = curVal(immStSorted, immAgreeKey);

        // ── Economy approval ──
        const econApprKey = "pol_vote:econapprove_approve:lik";
        const econCdDelta = waveDelta(econCdSorted, econApprKey);
        const econStDelta = waveDelta(econStSorted, econApprKey);
        const econCdCur   = curVal(econCdSorted, econApprKey);
        const econStCur   = curVal(econStSorted, econApprKey);

        // ── Income vs Prices: paycheck falling behind ──
        const incomeKey    = "income_v_prices__falling_behind_pred_n";
        const incomeCdDelta = waveDelta(cdSorted, incomeKey);
        const incomeStDelta = waveDelta(stSorted, incomeKey);
        const incomeCdCur   = curVal(cdSorted, incomeKey);
        const incomeStCur   = curVal(stSorted, incomeKey);

        // ── Most moved up issue: largest positive wave delta in the CD ──
        const mostMoved = (() => {
          const issGroup = groupsById?.pol_top_issues;
          if (!issGroup) return null;
          let best = null;
          issGroup.keys.forEach((k) => {
            const dCd = waveDelta(issCdSorted, k);
            const dSt = waveDelta(issStSorted, k);
            if (dCd != null && dCd > 0) {
              if (!best || dCd > best.dCd)
                best = { label: issGroup.labels?.[k] || k, dCd, dSt, cdCur: curVal(issCdSorted, k), stCur: curVal(issStSorted, k), diff: dCd - (dSt ?? 0), mode: "delta" };
            }
          });
          // Fallback: biggest current CD vs state gap
          if (!best && topIssues?.data?.length) {
            const top = topIssues.data
              .filter((d) => Number.isFinite(d.district) && Number.isFinite(d.state))
              .map((d) => ({ ...d, diff: d.district - d.state }))
              .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))[0];
            if (top) best = { label: top.label, dCd: null, dSt: null, cdCur: top.district, stCur: top.state, diff: top.diff, mode: "current" };
          }
          return best;
        })();

        const items = [
          {
            id: "imm_attitudes",
            title: "Immigration approval",
            cdVal:    immCdCur,
            stVal:    immStCur,
            cdDelta:  immCdDelta,
            stDelta:  immStDelta,
            diffDelta: immCdDelta != null && immStDelta != null ? immCdDelta - immStDelta : null,
          },
          {
            id: "econ",
            title: "Economy approval",
            cdVal:    econCdCur,
            stVal:    econStCur,
            cdDelta:  econCdDelta,
            stDelta:  econStDelta,
            diffDelta: econCdDelta != null && econStDelta != null ? econCdDelta - econStDelta : null,
          },
          {
            id: "income_vs_prices",
            title: "Paycheck falling behind prices",
            cdVal:    incomeCdCur,
            stVal:    incomeStCur,
            cdDelta:  incomeCdDelta,
            stDelta:  incomeStDelta,
            diffDelta: incomeCdDelta != null && incomeStDelta != null ? incomeCdDelta - incomeStDelta : null,
          },
          mostMoved ? {
            id: "mostmoved",
            title: `Most moved up: ${mostMoved.label}`,
            cdVal:    mostMoved.cdCur,
            stVal:    mostMoved.stCur,
            cdDelta:  mostMoved.mode === "delta" ? mostMoved.dCd : null,
            stDelta:  mostMoved.mode === "delta" ? mostMoved.dSt : null,
            diffDelta: mostMoved.diff,
          } : null,
        ].filter(Boolean);

        setKeyChanges({ loading: false, items });
      } catch {
        if (!isActive) return;
        setTopIssues({
          loading: false,
          latestWave: null,
          data: [],
          stateTop: [],
          cdTop: [],
          varianceText: null,
          varianceDiffs: null,
          colorMap: {},
        });
        setKeyChanges({ loading: false, items: [] });
      }
    })();

    return () => {
      isActive = false;
    };
  }, [stateFips, district, districtName]);

  /* ── VI predictions (CD-level) ── */
  const [viRows, setViRows] = useState([]);
  const [viRow, setViRow] = useState(null);
  const stateAbbr = String(state || "").toUpperCase();
  useEffect(() => {
    let isActive = true;

    const load = async () => {
      try {
        const res = await fetch("/api/cd_vi_preds/");
        if (!res.ok) throw new Error(`vi fetch failed: ${res.status}`);

        // Handle both JSON (with possible NaN values) and Arrow IPC
        const contentType = res.headers.get("content-type") || "";
        let rows;
        if (contentType.includes("application/json") || contentType.includes("text")) {
          const text = await res.text();
          // Backend emits NaN (valid JS, not valid JSON) — sanitise before parsing
          rows = JSON.parse(text.replace(/:\s*NaN/g, ": null"));
        } else {
          const buf = await res.arrayBuffer();
          const table = tableFromIPC(buf);
          rows = table.toArray().map((r) => r.toJSON());
        }

        if (!isActive) return;
        setViRows(rows || []);

        const distKey = String(district || "").padStart(2, "0");
        const isAtLarge = distKey === "00" || (stateAbbr === "AK" && distKey === "01");
        const keys = [
          `${stateAbbr}-${distKey}`,
          `${stateAbbr}-${Number(distKey)}`,
          isAtLarge ? `${stateAbbr}-AL` : null,
          `${stateAbbr}-00`,
        ]
          .filter(Boolean)
          .map((k) => k.toUpperCase());

        const match = (rows || []).find((r) =>
          keys.includes(String(r?.state_CD119 || "").toUpperCase())
        );
        setViRow(match || null);
      } catch (err) {
        console.error("[VI] fetch error:", err);
        if (!isActive) return;
        setViRows([]);
        setViRow(null);
      }
    };

    if (stateAbbr) load();
    return () => { isActive = false; };
  }, [stateAbbr, district]);

  /* ── Demographic data (reuse KeyInfo block) ── */
  const [demoRow, setDemoRow] = useState(null);
  const [demoBenchmark, setDemoBenchmark] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    if (!state || district == null) return;
    let isActive = true;
    const fetchDemo = async () => {
      try {
        setDemoLoading(true);
        const resolvedFips = stateFips || STATE_ABBR_TO_FIPS[String(state).toUpperCase()];
        const distStr = String(district);
        const distCandidates = new Set([
          distStr,
          distStr.padStart(2, "0"),
          distStr.padStart(3, "0"),
          distStr.padStart(4, "0"),
          distStr.padStart(5, "0"),
        ]);
        const areaCandidates = new Set(
          Array.from(distCandidates).map((d) => `${resolvedFips || ""}${d}`)
        );
        const normalizeDigits = (v) => String(v ?? "").replace(/\D/g, "");

        const [cdRes, stateRes] = await Promise.all([
          fetch(`/api/cd_political_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          }),
          fetch(`/api/states_political_data_arrow/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ stateCode: resolvedFips }),
          }),
        ]);

        if (!cdRes.ok || !stateRes.ok) throw new Error("demo fetch failed");

        const cdBuf = await cdRes.arrayBuffer();
        const cdTable = tableFromIPC(cdBuf);
        const cdRows = rowsFromTable(cdTable);

        const stateBuf = await stateRes.arrayBuffer();
        const stateTable = tableFromIPC(stateBuf);
        const stateRows = rowsFromTable(stateTable);

        const match = cdRows.find((r) => {
          const rStateRaw = normalizeDigits(r?.state_fips ?? r?.state ?? "");
          const rState = rStateRaw ? rStateRaw.padStart(2, "0") : "";
          const rDist = normalizeDigits(r?.dist_fips ?? "");
          const rArea = normalizeDigits(r?.area_id ?? "");
          const hasState = !!rState && rState !== "00";
          const stateOk = !resolvedFips || !hasState || rState === String(resolvedFips).padStart(2, "0");
          const distOk = rDist ? distCandidates.has(rDist) : false;
          const areaOk = rArea ? (areaCandidates.has(rArea) || distCandidates.has(rArea)) : false;
          return stateOk && (distOk || areaOk);
        });

        const bench = stateRows?.[0] || null;

        if (isActive) {
          setDemoRow(match || null);
          setDemoBenchmark(bench || null);
        }
      } catch (e) {
        if (isActive) {
          setDemoRow(null);
          setDemoBenchmark(null);
        }
      } finally {
        if (isActive) setDemoLoading(false);
      }
    };
    fetchDemo();
    return () => {
      isActive = false;
    };
  }, [state, district, stateFips]);

  /* ── Representative data ── */
  const [repData, setRepData] = useState(null);

  useEffect(() => {
    if (!state || district == null) return;
    loadFullRepData(state, district).then(setRepData);
  }, [state, district]);

  /* ── Caption wrap detection (same as original) ── */
  const captionRef = useRef(null);
  const [isWrapped, setIsWrapped] = useState(false);

  useEffect(() => {
    if (!captionRef.current) return;
    setIsWrapped(captionRef.current.scrollWidth > captionRef.current.clientWidth);
  }, [districtName]);

  const reportLoading = charts.loading || demoLoading || topIssues.loading || keyChanges.loading;

  // Whether the report actually has wave-over-wave deltas (2+ waves): true when any
  // Key Callout carries a genuine wave delta (same rule as MovementCard's hasWaveDelta).
  // Drives the Key Callouts caption — replaces the old literal "{delta shown when 2+ waves}".
  const hasWaveDeltas = (keyChanges.items || []).some(
    (it) => it?.cdDelta != null && Math.abs(it.cdDelta) <= 20
  );

  // Signal the render route (if present) once the core data has settled. The rep
  // photo + VI-preds render progressively and are covered by the route's image
  // drain / paint buffer; this fires on the component's own aggregate loading flag.
  useEffect(() => {
    if (!reportLoading && typeof onReady === "function") onReady();
  }, [reportLoading, onReady]);

  return (
    <Box
      id={id || "report-content"}
      sx={{
        backgroundColor: "#ffffff",
        color: "#0f172a",
        width: "794px",
        height: "1123px",
        overflow: "hidden",
        mx: "auto",
        p: "16px",
        border: "1px solid #dde3ed",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        position: "relative",
        fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
      }}
    >
      {reportLoading && (
        <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "14px", py: "9px", borderRadius: "8px", bgcolor: "white", border: "1px solid #e2e8f0", boxShadow: "0 6px 20px rgba(15,23,42,0.10)" }}>
            <CircularProgress size={16} thickness={5} />
            <Typography sx={{ fontSize: "0.75rem", color: "#475569", fontWeight: 600 }}>Building report…</Typography>
          </Box>
        </Box>
      )}

      {/* ── HEADER ── */}
      <Box sx={{ flexShrink: 0 }}>
        <DistrictTopHeader
          districtName={districtName}
          state={state}
          district={district}
          repData={repData}
          topVI={topVI}
          viRow={viRow}
        />
        <RepCard repData={repData} />
      </Box>

      {/* ── MAP + VOTE COMPARISON ROW ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", flexShrink: 0 }}>

        {/* Past Vote Map */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography sx={{ fontSize: "0.60rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.10em", color: "#64748b" }}>
            House 2024 Result
          </Typography>
          <Box sx={{ height: 175, borderRadius: "8px", overflow: "hidden", border: "1px solid #e8edf4", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PastVoteCDMap
              stateFips={stateFips || STATE_ABBR_TO_FIPS[String(state || "").toUpperCase()]}
              district={district}
              houseParty={demoRow?.house_2024_party}
              width={320}
              height={175}
            />
          </Box>
        </Box>

        {/* Vote share comparison — bar chart */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography sx={{ fontSize: "0.60rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.10em", color: "#64748b" }}>
            Past Result vs. Vote Intention
          </Typography>
          <Box sx={{ height: 175, borderRadius: "8px", border: "1px solid #e8edf4", bgcolor: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: "12px" }}>
            <PastVsViBar
              pastDem={demoRow?.house_2024_dem != null ? demoRow.house_2024_dem : null}
              pastRep={demoRow?.house_2024_rep != null ? demoRow.house_2024_rep : null}
              viDem={viRow?.dem_pct != null ? Number(viRow.dem_pct) : null}
              viRep={viRow?.rep_pct != null ? Number(viRow.rep_pct) : null}
            />
          </Box>
        </Box>

        {/* VI Map */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography sx={{ fontSize: "0.60rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.10em", color: "#64748b" }}>
            Vote Intention by District (Q1)
          </Typography>
          <Box sx={{ height: 175, borderRadius: "8px", overflow: "hidden", border: "1px solid #e8edf4", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <VIStateMap
              stateFips={stateFips || STATE_ABBR_TO_FIPS[String(state || "").toUpperCase()]}
              district={district}
              viRows={viRows}
              width={320}
              height={175}
            />
          </Box>
        </Box>
      </Box>

      {/* ── DEMOGRAPHIC PROFILE ── */}
      <Box sx={{ flexShrink: 0 }}>
        <DistrictDemographicBlock
          demoRow={demoRow}
          demoBenchmark={demoBenchmark}
          districtName={districtName}
          stateName={stateName || state}
          demoLoading={demoLoading}
        />
      </Box>

      {/* ── BOTTOM: TOP ISSUES (left) + KEY OPINION METRICS (right) ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1.1fr 0.85fr", gap: "20px", flex: "1 1 0", minHeight: 0 }}>

        {/* Left: Top Issues with district vs state comparison context */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "6px" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em", color: "#0f172a" }}>
              Top Issues
            </Typography>
            <Typography sx={{ fontSize: "0.58rem", color: "#94a3b8", fontStyle: "italic" }}>
              Latest wave · District vs. State
            </Typography>
          </Box>
          <BarChartAutoHeight
            data={topIssues.data}
            districtName={districtName}
            stateName={stateName || state}
            varianceDiffs={topIssues.varianceDiffs}
            varianceText={topIssues.varianceText}
            colorMap={topIssues.colorMap}
          />
        </Box>

        {/* Right: Opinion metrics — structured as comms-ready signals */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", minHeight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "0px" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em", color: "#0f172a" }}>
              Key Callouts
            </Typography>
            <Typography sx={{ fontSize: "0.58rem", color: "#94a3b8", fontStyle: "italic" }}>
              District vs. State{hasWaveDeltas ? " · Δ vs. prior wave" : ""}
            </Typography>
          </Box>

          {keyChanges.loading ? (
            [1,2,3,4].map((i) => (
              <Box key={i} sx={{ border: "1px solid #e8edf4", borderRadius: "8px", p: "12px", bgcolor: "#fff", minHeight: 76 }}>
                <Skeleton variant="text" width="45%" height={11} sx={{ mb: "6px" }} />
                <Skeleton variant="text" width="65%" height={26} />
                <Skeleton variant="text" width="85%" height={10} sx={{ mt: "6px" }} />
              </Box>
            ))
          ) : (
            keyChanges.items.map((item) => (
              <MovementCard
                key={item.id}
                title={item.title}
                cdVal={item.cdVal}
                stVal={item.stVal}
                cdDelta={item.cdDelta}
                stDelta={item.stDelta}
                diffDelta={item.diffDelta}
                showDeltaAsPrimary={item.id === "mostmoved"}
                loading={false}
              />
            ))
          )}

          {/* Footer note for comms users */}
          {!keyChanges.loading && (
            <Box sx={{ mt: "auto", pt: "6px", borderTop: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontSize: "0.58rem", color: "#b0bac8", lineHeight: 1.5 }}>
                Deltas reflect wave-over-wave movement. Where only a single wave is available, current values are shown without trend indicators.
              </Typography>
            </Box>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default DistrictReportView;
